import { useState, useEffect } from 'react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';

interface LivenessStepProps {
  sessionId: string;
  onComplete: () => void;
  onError: (error: Error) => void;
  onRetry?: () => void;
}

const REGION = import.meta.env.VITE_AWS_REGION || 'eu-west-1';

export function LivenessStep({ sessionId, onComplete, onError, onRetry }: LivenessStepProps) {
  const [showCustomError, setShowCustomError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [hasStarted, setHasStarted] = useState(false); // Para controlar el inicio manualmente

  // 🔥 FUERZA BRUTA: ELIMINAR TODO LO QUE AWS AÑADE
  useEffect(() => {
    // 1. INYECTAR CSS GLOBAL CON !IMPORTANT - NUCLEAR
    const style = document.createElement('style');
    style.textContent = `
      /* 🔥 ELIMINAR SELECTOR DE CÁMARA - TODAS LAS FORMAS POSIBLES */
      [data-amplify-liveness] select,
      [data-amplify-liveness] label,
      [data-amplify-liveness] label[for],
      [data-amplify-liveness] *[class*="select"],
      [data-amplify-liveness] *[class*="Select"],
      [data-amplify-liveness] *[class*="camera"],
      [data-amplify-liveness] *[class*="Camera"],
      [data-amplify-liveness] div:has(select),
      [data-amplify-liveness] div:has(> select),
      [data-amplify-liveness] *:has(select) {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        position: absolute !important;
        left: -9999px !important;
        width: 0 !important;
        height: 0 !important;
        max-height: 0 !important;
        overflow: hidden !important;
      }

      /* Buscar por contenido de texto "Camera" */
      [data-amplify-liveness] *:not(video):not(canvas) {
        &:has-text("Camera") {
          display: none !important;
        }
      }

      /* OCULTAR solo botones de cerrar y grabación */
      [data-amplify-liveness] button[aria-label*="Cancel"],
      [data-amplify-liveness] button[aria-label*="Close"],
      [data-amplify-liveness] button[aria-label*="cancel"],
      [data-amplify-liveness] button[aria-label*="close"],
      [data-amplify-liveness] [data-amplify-liveness-recording-icon],
      [data-amplify-liveness] *[class*="recording"],
      [data-amplify-liveness] *[class*="Recording"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
      }

      /* 🔥🔥🔥 NUCLEAR: OCULTAR TODO TEXTO Y CONTROLES DE AWS 🔥🔥🔥 */
      [data-amplify-liveness] [class*="hint"],
      [data-amplify-liveness] [class*="Hint"],
      [data-amplify-liveness] [class*="Toast"],
      [data-amplify-liveness] [class*="toast"],
      [data-amplify-liveness] [class*="Alert"],
      [data-amplify-liveness] [class*="alert"],
      [data-amplify-liveness] [class*="Message"],
      [data-amplify-liveness] [class*="message"],
      [data-amplify-liveness] [class*="Overlay"],
      [data-amplify-liveness] [class*="overlay"],
      [data-amplify-liveness] [class*="instruction"],
      [data-amplify-liveness] [class*="Instruction"],
      [data-amplify-liveness] [role="alert"],
      [data-amplify-liveness] [role="status"],
      [data-amplify-liveness] button,
      [data-amplify-liveness] [class*="recording"],
      [data-amplify-liveness] [class*="Recording"],
      [data-amplify-liveness] [class*="cancel"],
      [data-amplify-liveness] [class*="Cancel"],
      [data-amplify-liveness] [class*="close"],
      [data-amplify-liveness] [class*="Close"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        position: absolute !important;
        left: -99999px !important;
        top: -99999px !important;
        width: 0 !important;
        height: 0 !important;
        font-size: 0 !important;
        color: transparent !important;
        background: transparent !important;
      }
      
      /* Ocultar CUALQUIER texto dentro del contenedor AWS excepto video/canvas */
      [data-amplify-liveness] p,
      [data-amplify-liveness] span,
      [data-amplify-liveness] div > span,
      [data-amplify-liveness] h1, [data-amplify-liveness] h2, [data-amplify-liveness] h3,
      [data-amplify-liveness] label {
        font-size: 0 !important;
        color: transparent !important;
        background: transparent !important;
        text-indent: -99999px !important;
      }

      /* RESPONSIVE - En desktop centrar y limitar ancho */
      @media (min-width: 768px) {
        [data-amplify-liveness] {
          max-width: 600px !important;
          margin: 0 auto !important;
        }
      }

      /* Video y óvalo tamaño natural - VISIBLE */
      [data-amplify-liveness] video {
        display: block !important;
        width: 100% !important;
        height: auto !important;
        min-height: 300px !important;
        object-fit: cover !important;
        visibility: visible !important;
        opacity: 1 !important;
        position: relative !important;
        z-index: 1 !important;
      }

      /* Canvas también visible */
      [data-amplify-liveness] canvas {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        position: relative !important;
        z-index: 1 !important;
      }

      /* Óvalo visible */
      [data-amplify-liveness] [data-amplify-liveness-oval],
      [data-amplify-liveness] svg {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      }

      /* El contenedor principal DEBE ser visible */
      [data-amplify-liveness] [data-amplify-liveness-detector] {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        min-height: 300px !important;
      }

      /* Eliminar padding/margin extra */
      [data-amplify-liveness] {
        padding: 0 !important;
      }
    `;
    document.head.appendChild(style);

    // 2. 🔥🔥🔥 ULTRA NUCLEAR: OVERRIDE ESTILOS INLINE 🔥🔥🔥
    const destroyUnwantedElements = () => {
      const livenessContainer = document.querySelector('[data-amplify-liveness]');
      if (!livenessContainer) return;
      
      const customHint = document.getElementById('custom-hint');
      
      // 🔥 RECORRER TODOS LOS ELEMENTOS
      livenessContainer.querySelectorAll('*').forEach(el => {
        const htmlEl = el as HTMLElement;
        const text = el.textContent?.trim() || '';
        const tag = el.tagName;
        
        // Preservar video, canvas, svg
        if (tag === 'VIDEO' || tag === 'CANVAS' || tag === 'SVG') return;
        if (el.querySelector('video') || el.querySelector('canvas') || el.querySelector('svg')) return;
        
        // 🔥 CAPTURAR TEXTO DEL HINT
        if (customHint && text.length > 2 && text.length < 60) {
          if (text.includes('cerca') || text.includes('leja') || text.includes('Acérc') || 
              text.includes('Aléj') || text.includes('Centra') || text.includes('Mantén') ||
              text.includes('Move') || text.includes('Hold') || text.includes('closer')) {
            customHint.textContent = text;
          }
        }
        
        // 🔥 FORZAR INVISIBILIDAD CON setAttribute style
        if (tag === 'BUTTON' || tag === 'SELECT' || tag === 'LABEL') {
          htmlEl.setAttribute('style', 'display:none!important;visibility:hidden!important;opacity:0!important;width:0!important;height:0!important;overflow:hidden!important;position:absolute!important;left:-9999px!important;');
        }
        
        // 🔥 OCULTAR CUALQUIER SPAN/P/DIV CON TEXTO DE HINT
        if ((tag === 'SPAN' || tag === 'P' || tag === 'DIV') && text.length > 0) {
          const hasHintText = text.includes('cerca') || text.includes('leja') || text.includes('más') ||
                             text.includes('Acérc') || text.includes('Aléj') || text.includes('×') ||
                             text.includes('Centra') || text.includes('rostro') || text.includes('Move') ||
                             text.includes('Hold') || text.includes('closer') || text.includes('further');
          
          if (hasHintText) {
            htmlEl.setAttribute('style', 'display:none!important;visibility:hidden!important;opacity:0!important;font-size:0!important;color:transparent!important;width:0!important;height:0!important;overflow:hidden!important;');
          }
        }
        
        // 🔥 OCULTAR ELEMENTOS CON CLASES DE HINT/TOAST/ALERT
        const className = htmlEl.className?.toString().toLowerCase() || '';
        if (className.includes('hint') || className.includes('toast') || className.includes('alert') ||
            className.includes('message') || className.includes('overlay') || className.includes('recording') ||
            className.includes('indicator') || className.includes('instruction')) {
          htmlEl.setAttribute('style', 'display:none!important;visibility:hidden!important;opacity:0!important;');
        }
        
        // 🔥 OCULTAR POR ROLE
        const role = htmlEl.getAttribute('role');
        if (role === 'alert' || role === 'status') {
          htmlEl.setAttribute('style', 'display:none!important;visibility:hidden!important;opacity:0!important;');
        }
      });
    };

    // 3. EJECUTAR INMEDIATAMENTE Y MÚLTIPLES VECES
    destroyUnwantedElements();
    setTimeout(destroyUnwantedElements, 5);
    setTimeout(destroyUnwantedElements, 10);
    setTimeout(destroyUnwantedElements, 20);
    setTimeout(destroyUnwantedElements, 50);
    setTimeout(destroyUnwantedElements, 100);
    setTimeout(destroyUnwantedElements, 200);
    setTimeout(destroyUnwantedElements, 500);

    // 4. INTERVAL MEGA AGRESIVO - CADA 2MS
    const interval = setInterval(destroyUnwantedElements, 2);

    // 5. MUTATIONOBSERVER - DETECTAR CAMBIOS EN EL DOM Y CAPTURAR HINTS
    const observer = new MutationObserver(() => {
      destroyUnwantedElements();
      
      // Capturar texto del hint de AWS y mostrarlo en nuestro hint
      const awsHint = document.querySelector('[data-amplify-liveness] [class*="hint"]') as HTMLElement;
      const customHint = document.getElementById('custom-hint');
      
      if (awsHint && customHint && awsHint.textContent) {
        const hintText = awsHint.textContent.trim();
        if (hintText && hintText.length > 0) {
          customHint.textContent = hintText;
        }
      }
    });

    const livenessContainer = document.querySelector('[data-amplify-liveness]');
    if (livenessContainer) {
      observer.observe(livenessContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true, // Detectar cambios de texto
      });
    }

    // También verificar periodicamente el hint - buscar en múltiples selectores
    const hintInterval = setInterval(() => {
      const customHint = document.getElementById('custom-hint');
      if (!customHint) return;
      
      // Buscar en múltiples posibles ubicaciones del hint de AWS
      const selectors = [
        '[data-amplify-liveness] [class*="hint"]',
        '[data-amplify-liveness] [class*="Hint"]',
        '[data-amplify-liveness] p',
        '[data-amplify-liveness] span',
      ];
      
      for (const selector of selectors) {
        const elements = document.querySelectorAll(selector);
        elements.forEach((el) => {
          const text = el.textContent?.trim() || '';
          // Solo capturar si el texto parece un hint válido
          if (text.length > 3 && text.length < 50 && 
              (text.includes('cerca') || text.includes('leja') || text.includes('rostro') || 
               text.includes('luz') || text.includes('Mantén') || text.includes('Centra') ||
               text.includes('detectado') || text.includes('persona') || text.includes('Verificando') ||
               text.includes('Conectando') || text.includes('Listo'))) {
            customHint.textContent = text;
          }
        });
      }
    }, 150);

    return () => {
      clearInterval(interval);
      clearInterval(hintInterval);
      observer.disconnect();
      style.remove();
    };
  }, [sessionId, retryKey]);

  console.log('🎥 LivenessStep rendering with sessionId:', sessionId, 'region:', REGION);

  const handleAnalysisComplete = async () => {
    console.log('✅ Liveness analysis complete');
    setShowCustomError(false);
    onComplete();
  };

  const handleError = (error: any) => {
    const errorMessage = String(error?.error?.message || error?.message || error);
    console.error('❌ Liveness error:', errorMessage);

    // Mostrar nuestra pantalla de error personalizada
    setShowCustomError(true);
    onError(new Error(errorMessage));
  };

  // Reintentar
  const handleRetry = async () => {
    console.log('🔄 Retrying...');
    setShowCustomError(false);
    setRetryKey(prev => prev + 1);

    // Crear nueva sesión si hay callback
    if (onRetry) {
      await onRetry();
    }
  };

  // Componente de error personalizado para reemplazar el del SDK
  const CustomErrorView = () => {
    return (
      <div className="bg-white rounded-2xl p-6 text-center m-4">
        <div className="text-5xl mb-4">📷</div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          No se puede acceder a la cámara
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          Necesitamos acceso a tu cámara para verificar tu identidad.
        </p>

        <div className="bg-gray-50 rounded-xl p-4 text-left mb-6">
          <p className="font-medium text-gray-800 mb-3">Prueba esto:</p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
            <li>Cierra otras apps que usen la cámara</li>
            <li>
              <span className="font-medium">Navegador → Ajustes → Cámara → </span>
              Permitir
            </li>
            <li>Cierra el navegador completamente y vuelve a abrir</li>
          </ol>
        </div>

        <button
          onClick={handleRetry}
          className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reintentar acceso a cámara
        </button>

        <button
          onClick={() => window.location.reload()}
          className="w-full mt-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-colors"
        >
          Recargar página
        </button>
      </div>
    );
  };

  // Si mostramos nuestro error personalizado
  if (showCustomError) {
    return (
      <div className="space-y-4">
        <div className="text-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Prueba de Vida</h2>
        </div>
        <CustomErrorView />
      </div>
    );
  }

  // PANTALLA PREPARACIÓN - OPTIMIZADA PARA PC Y MÓVIL
  if (!hasStarted) {
    return (
      <div className="flex items-center justify-center px-4 py-3 md:px-8 md:py-6 min-h-[55vh] md:min-h-[65vh]">
        <div className="flex flex-col items-center text-center w-full max-w-sm md:max-w-md gap-3 md:gap-4">
          
          {/* RECUADRO PRINCIPAL */}
          <div className="flex-shrink-0 w-full bg-gradient-to-br from-sky-50 to-blue-50 border-2 border-sky-400 rounded-2xl p-4 md:p-8 shadow-lg">
            {/* Header */}
            <div className="mb-3 md:mb-6">
              <span className="text-3xl md:text-5xl">📸</span>
              <h2 className="text-base md:text-2xl font-bold text-gray-900 mt-1.5 md:mt-3">Prueba de Vida</h2>
              <p className="text-[10px] md:text-base text-gray-600 mt-0.5 md:mt-1">Verificaremos que eres una persona real.</p>
            </div>

            {/* Instrucciones */}
            <div className="mb-4 md:mb-6">
              <h3 className="text-xs md:text-base font-semibold text-sky-800 mb-2 md:mb-4 flex items-center justify-start gap-1.5 md:gap-2">
                <svg className="w-3.5 h-3.5 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Para la verificación:
              </h3>
              <ul className="space-y-1.5 md:space-y-3 text-left text-gray-700 text-[10px] md:text-sm">
                <li className="flex items-center gap-1.5 md:gap-3">
                  <span className="text-emerald-600 text-sm md:text-xl font-bold">✓</span> Buena iluminación
                </li>
                <li className="flex items-center gap-1.5 md:gap-3">
                  <span className="text-emerald-600 text-sm md:text-xl font-bold">✓</span> Asegúrate de estar solo en el encuadre
                </li>
                <li className="flex items-center gap-1.5 md:gap-3">
                  <span className="text-emerald-600 text-sm md:text-xl font-bold">✓</span> Coloca tu rostro dentro del óvalo
                </li>
                <li className="flex items-center gap-1.5 md:gap-3">
                  <span className="text-emerald-600 text-sm md:text-xl font-bold">✓</span> Mantén la posición 3-5 segundos
                </li>
              </ul>
            </div>

            {/* Botón */}
            <button
              onClick={() => setHasStarted(true)}
              className="w-full bg-gradient-to-r from-blue-600 to-sky-700 hover:from-blue-700 hover:to-sky-800 text-white font-semibold py-3 md:py-5 rounded-xl shadow-lg text-sm md:text-lg transition-all"
            >
              Iniciar Verificación
            </button>
          </div>

          {/* Mensaje de privacidad */}
          <p className="text-[10px] md:text-sm text-gray-500 text-center flex items-center justify-center gap-1">
            <span>📷</span> Tu cámara se activará. No se graba ni almacena tu rostro.
          </p>
        </div>
      </div>
    );
  }

  // COMPONENTE AWS (con disableStartScreen={true} para evitar selector)
  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-2">
      {/* NUESTRO HINT PERSONALIZADO - FUERA del iframe de AWS */}
      <div id="custom-hint-container" className="w-full flex justify-center mb-3">
        <div id="custom-hint" className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg">
          Coloca tu rostro en el óvalo
        </div>
      </div>

      {/* Video con óvalo */}
      <div className="w-full relative" style={{ minHeight: '400px' }}>
        <FaceLivenessDetector
          key={`${sessionId}-${retryKey}`}
          sessionId={sessionId}
          region={REGION}
          onAnalysisComplete={handleAnalysisComplete}
          onError={handleError}
          disableStartScreen={true}
          components={{
            PhotosensitiveWarning: () => null,
            ErrorView: CustomErrorView,
          }}
          displayText={{
            // ✅ HINTS EN ESPAÑOL - CLAROS Y ÚTILES
            hintMoveFaceFrontOfCameraText: 'Coloca tu rostro frente a la cámara',
            hintTooManyFacesText: 'Solo debe haber una persona',
            hintFaceDetectedText: 'Rostro detectado ✓',
            hintCanNotIdentifyText: 'No detectamos tu rostro',
            hintTooCloseText: 'Aléjate un poco',
            hintTooFarText: 'Acércate más',
            hintConnectingText: 'Conectando...',
            hintVerifyingText: 'Verificando...',
            hintCheckCompleteText: '¡Listo!',
            hintIlluminationTooBrightText: 'Hay demasiada luz',
            hintIlluminationTooDarkText: 'Necesitas más luz',
            hintIlluminationNormalText: 'Iluminación correcta ✓',
            hintHoldFaceForFreshnessText: 'Mantén la posición...',
            hintCenterFaceText: 'Centra tu rostro en el óvalo',
            hintCenterFaceInstructionText: 'Coloca tu rostro dentro del óvalo',
            hintFaceOffCenterText: 'Centra tu rostro',
            hintMatchIndicatorText: '',

            // === CÁMARA ===
            cameraMinSpecificationsHeadingText: 'Cámara no compatible',
            cameraMinSpecificationsMessageText: 'Tu cámara no cumple los requisitos mínimos.',
            cameraNotFoundHeadingText: 'Cámara no encontrada',
            cameraNotFoundMessageText: 'No detectamos ninguna cámara.',
            retryCameraPermissionsText: 'Reintentar',
            waitingCameraPermissionText: '',
            a11yVideoLabelText: 'Video',

            // === INSTRUCCIONES ===
            goodFitCaptionText: '',
            goodFitAltText: '',
            tooFarCaptionText: '',
            tooFarAltText: '',
            startScreenBeginCheckText: 'Iniciar Verificación',

            // === FOTOSENSIBILIDAD ===
            photosensitivityWarningHeadingText: '',
            photosensitivityWarningBodyText: '',
            photosensitivityWarningInfoText: '',
            photosensitivityWarningLabelText: '',

            // === STREAMING ===
            recordingIndicatorText: '',
            cancelLivenessCheckText: '',

            // === ERRORES ===
            errorLabelText: 'Error',
            connectionTimeoutHeaderText: 'Sin conexión',
            connectionTimeoutMessageText: 'Verifica tu internet.',
            timeoutHeaderText: 'Tiempo agotado',
            timeoutMessageText: 'Intenta de nuevo.',
            faceDistanceHeaderText: 'Distancia incorrecta',
            faceDistanceMessageText: 'Ajusta la distancia.',
            multipleFacesHeaderText: 'Múltiples rostros',
            multipleFacesMessageText: 'Solo tu rostro debe estar visible.',
            clientHeaderText: 'Error',
            clientMessageText: 'Intenta de nuevo.',
            serverHeaderText: 'Error del servidor',
            serverMessageText: 'Intenta más tarde.',
            landscapeHeaderText: 'Gira tu dispositivo',
            landscapeMessageText: 'Usa modo vertical.',
            portraitMessageText: '',
            tryAgainText: 'Intentar de nuevo',
          }}
        />
      </div>

      {/* Instrucción clara FLOTANTE ABAJO - z-index alto para estar encima */}
      <div className="absolute bottom-20 left-0 right-0 z-50 px-4 pointer-events-none">
        <div className="max-w-md mx-auto bg-gradient-to-r from-sky-500 to-blue-500 text-white text-center py-3 px-4 rounded-xl shadow-2xl">
          <p className="text-sm font-semibold">
            Acerca tu rostro al óvalo y mantén 3-5 segundos
          </p>
        </div>
      </div>

      {/* Mensaje de seguridad FLOTANTE MUY ABAJO */}
      <div className="absolute bottom-6 left-0 right-0 z-50 px-4 pointer-events-none">
        <div className="max-w-sm mx-auto flex items-center justify-center gap-1.5 text-xs text-gray-600 bg-white/90 backdrop-blur-sm py-2 px-4 rounded-full shadow-lg">
          <svg className="w-3.5 h-3.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <span>Tu rostro no se graba · 100% seguro</span>
        </div>
      </div>
    </div>
  );
}
